import {useState, useEffect} from 'react';

import {
  countBy,
  groupBy,
  keyBy,
  each,
  size,
} from 'lodash-es';


/**
 * Builds a % score based on a list of student submissions.
 * @param {object[]} submissions
 * @returns {string}
 */
export const getQuizScoreFromSubmissions = (submissions) => {
  if (size(submissions) === 0) {
    return '-';
  } else if (size(submissions) < 4) {
    return `Completed ${size(submissions)}/4`;
  } else {
    const correctAnswersCount = countBy(submissions, 'answer_is_correct')[true];
    return `${correctAnswersCount / 4 * 100}%`;
  }
};

/**
 * Builds and returns a list of CSV data rows. Handles
 * student object shapes for small group assignments
 * (i.e., student.first_name and student.last_name) and
 * classroom assignments (i.e., student.user.first_name
 * and student.user.last_name).
 * @param {object[]} submissions
 * @param students
 * @returns {string[][]}
 */
export const buildCSVDataRows = (submissions, students) => {
  const submissionsGroupedByUser = groupBy(submissions, 'user');
  const rows = [['"Student Name"', '"Quiz Score"']];

  each(students, (student) => {
    const {user, unique_id, last_name, first_name} = student || {};
    const firstName = user?.first_name || first_name;
    const lastName = user?.last_name || last_name;
    const studentId = user?.id || unique_id;
    const studentName = `${lastName} ${firstName}`;
    const studentSubmissions = submissionsGroupedByUser[studentId];

    rows.push([studentName, getQuizScoreFromSubmissions(studentSubmissions)]);
  });

  return rows;
};

/**
 * Builds a string that represents a CSV file.
 * @param {object[]} dataRows
 * @returns {string}
 */
export const buildCSVFileFromDataRows = (dataRows) => {
  let csvContent = '';
  dataRows.forEach((row) => {
    csvContent += row.join(',') + '\r\n';
  });
  return csvContent;
};

/**
 * @param students
 * @param interactiveVideos
 * @returns {object[]}
 */
export const useCSVFiles = (students, interactiveVideos) => {
  const interactiveVideosById = keyBy(interactiveVideos, 'content_id');
  const [submissionsByContentId, setSubmissionsByContentIdData] = useState({});
  const filters = {
    interactive_video_ids: Object.keys(interactiveVideosById),
    submitted_by_users: students?.map(({user, unique_id}) => {
      return user?.id || unique_id;
    }),
  };
  const queryParams = new URLSearchParams(filters);
  const videoQuizSubmissionUrl = `/api/v3/interactive-video-link-quiz-submission/?${queryParams}`;

  useEffect(() => {
    fetch(videoQuizSubmissionUrl)
      .then((response) => response.json())
      .then((data) => setSubmissionsByContentIdData(groupBy(data, 'interactive_video')));
  }, []);

  const files = [];
  for (const contentId in interactiveVideosById) {
    const contentSubmissions = submissionsByContentId[contentId];
    const dataRows = buildCSVDataRows(contentSubmissions, students);
    const csvFile = buildCSVFileFromDataRows(dataRows);
    const blob = new Blob([csvFile], {type: 'text/csv;charset=utf-8;'});
    const video = interactiveVideosById[contentId];
    files.push({
      fileName: video.title,
      content: URL.createObjectURL(blob),
    });
  }
  return files;
};
